import React, { useEffect, useState } from 'react';
import { Layout, message, Button, Form, Input, Switch, Select, Tabs, Drawer } from 'antd';
import { PageHeader } from '@ant-design/pro-components';
import { useNavigate, useParams } from 'react-router-dom';
import MemberSidebar from './components/member.sidebar.component';
import MemberFooter from './components/member.footer.component';
import { useDispatch, useSelector } from 'react-redux';
import dot from 'dot-object';
import MediaSelect from './components/mediaSelect.field';
import EntityMedia from './entity.media.page';
import { API_CDN_URL } from '../services/config';
import {
    readTripCollection,
    createTripCollection,
    updateTripCollection
} from '../actions/entity.trip.collection'
import { userIsReadOnlyInEntity } from '../helpers/user';
import { useTranslation } from 'react-i18next';
import SeoFields from './components/seo.component';
import { readAllActiveTripTopicsTranslations } from '../actions/entity.trip.topic';

const layout = {
    labelCol: {
        span: 8,
    },
    wrapperCol: {
        span: 10,
    },
};

function TripCollection() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();
    const entities = useSelector(state => state.entity.entities);
    const currentEntity = entities ? entities.find(entity => entity.id === params.entityId) : null;
    const { Content } = Layout;
    const [form] = Form.useForm();
    const [tripCollectionTitle, setTripCollectionTitle] = useState('');
    const currentUser = useSelector(state => state.auth.user);
    const userIsReadOnly = userIsReadOnlyInEntity(currentUser, currentEntity);
    const requiredTripSupportAllLanguages = currentEntity && currentEntity.requiredTripSupportAllLanguages;
    const [addedLanguages, setAddedLanguages] = useState([]);
    const [mediaDrawerVisible, setMediaDrawerVisible] = useState(false);
    const [currentMediaFieldName, setCurrentMediaFieldName] = useState(null);
    const [collectionData, setCollectionData] = useState(null);
    const [topicOptions, setTopicOptions] = useState([]);

    const showMediaDrawer = (fieldName) => {
        setCurrentMediaFieldName(fieldName);
        setMediaDrawerVisible(true);
    };
    const onMediaDrawerClose = () => {
        setMediaDrawerVisible(false);
    };

    const onSelectMedia = (media) => {
        setMediaDrawerVisible(false);
        if (currentMediaFieldName && currentEntity) {
            let url = API_CDN_URL(currentEntity) + media.path;
            let newValue = {
                [currentMediaFieldName.join('.')]: url
            };
            newValue = dot.object(newValue);
            form.setFieldsValue(newValue);
        }
    }

    useEffect(() => {
        if (currentEntity) {
            if (currentEntity.requiredTripSupportAllLanguages) {
                setAddedLanguages(currentEntity.languages.filter(language => {
                    return language.status
                }).map(language => {
                    return {
                        label: language.name,
                        value: language.code
                    }
                }));
            } else {
                if (collectionData && typeof collectionData.languages !== 'undefined' && Array.isArray(collectionData.languages)) {
                    setAddedLanguages(currentEntity.languages.filter(language => {
                        return language.status && collectionData.languages.includes(language.code)
                    }).map(language => {
                        return {
                            label: language.name,
                            value: language.code
                        }
                    }));
                }
            }
            // Fetch the topics
            dispatch(readAllActiveTripTopicsTranslations(currentEntity.id)).then((response) => {
                if (typeof response.result !== 'undefined') {
                    setTopicOptions(response.result.map(topicTranslation => {
                        return {
                            label: topicTranslation.title,
                            value: topicTranslation.id,
                            language: topicTranslation.language
                        }
                    }));
                }
            }).catch(error => {
                console.log(error);
                message.error(error.message);
            });
        }
    }, [currentEntity, collectionData]);
    const languageOptions = !requiredTripSupportAllLanguages && currentEntity && currentEntity.languages ? currentEntity.languages.map(language => {
        return {
            label: language.name,
            value: language.code,
            disabled: !language.status
        }
    }) : [];

    const onFinish = (values) => {
        if (userIsReadOnly) {
            message.error(t('tripCollection.message.read-only'));
            return;
        }
        if (typeof params.collectionId !== 'undefined') {
            // Update the existing entity collection
            dispatch(updateTripCollection(params.entityId, params.collectionId, values)).then((response) => {
                if (typeof response.result !== 'undefined' && typeof response.result.id !== 'undefined') {
                    setTripCollectionTitle(t('tripCollection.title.edit'));
                    form.setFieldsValue(response.result);
                    setCollectionData(response.result);
                }
                message.success(t('tripCollection.message.update-success'));
            }).catch(error => {
                try {
                    let responseMessage = error.response.data.message;
                    message.error(responseMessage);
                } catch (e) {
                    console.log('Failed:', e);
                    message.error(JSON.stringify(e));
                }
            });
        } else {
            // Create new campaign
            dispatch(createTripCollection(params.entityId, values)).then((data) => {
                message.success(t('tripCollection.message.create-success'));
                if (data.result) {
                    navigate(`/entity/${params.entityId}/trip-collection/${data.result.id}`);
                } else {
                    navigate(`/entity/${params.entityId}/trip-collections`);
                }
            }).catch(error => {
                try {
                    let responseMessage = error.response.data.message;
                    message.error(responseMessage);
                } catch (e) {
                    console.log('Failed:', e);
                    message.error(JSON.stringify(e));
                }
            });
        }
    }

    useEffect(() => {
        if (typeof params.collectionId !== 'undefined') {
            setTripCollectionTitle(t('tripCollection.title.edit'));
            dispatch(readTripCollection(params.entityId, params.collectionId)).then((response) => {
                if (typeof response.result !== 'undefined' && typeof response.result.id !== 'undefined') {
                    setTripCollectionTitle(t('tripCollection.title.edit'));
                    form.setFieldsValue(response.result);
                    setCollectionData(response.result);
                } else {
                    message.error(t('tripCollection.message.error-reading'));
                    navigate(`/entity/${params.entityId}/trip-collections`);
                }
            }).catch(e => {
                message.error(t('tripCollection.message.error-reading'));
                navigate(`/entity/${params.entityId}/trip-collections`);
            });
        } else {
            setTripCollectionTitle(t('tripCollection.title.new'));
        }
    }, [params.collectionId, params.entityId, dispatch, navigate, form]);

    const onFinishFailed = (errorInfo) => {
        if (typeof errorInfo.errorFields !== 'undefined') {
            errorInfo.errorFields.forEach(field => {
                message.error(field.errors[0]);
            });
        }
    }

    const selectedLanguages = (options) => {
        if (Array.isArray(options)) {
            const filteredLanguages = languageOptions.filter(language => {
                if (options.includes(language.value)) {
                    return true;
                } else {
                    return false;
                }
            });
            console.log(options);
            console.log(filteredLanguages);
            setAddedLanguages(filteredLanguages);
        } else {
            setAddedLanguages([]);
        }
    }

    let extraButtons = [
        <Button key="cancel" type="primary" danger href={`/entity/${params.entityId}/trip-collections`}>{t('tripCollection.actions.cancel')}</Button>
    ];
    if (!userIsReadOnly) {
        extraButtons.push(<Button key="save" type="primary" htmlType="submit">{params.collectionId ? t('tripCollection.actions.update') : t('tripCollection.actions.save')}</Button>);
    }
    return <>
        <MemberSidebar />
        <Layout className="site-layout">
            <Content style={{ margin: '0 16px' }}>
                <div className="site-layout-background">
                    <Form
                        {...layout}
                        form={form}
                        name="entity-collection"
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                        initialValues={{
                            status: true
                        }}>
                        <PageHeader
                            className="site-page-header"
                            onBack={() => navigate(`/entity/${params.entityId}/trip-collections`)}
                            title={tripCollectionTitle}
                            extra={extraButtons}
                        />
                        <>
                            <Form.Item
                                label={t('tripCollection.field.status.label')}
                                name="status"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                            <Form.Item
                                label={t('tripCollection.field.slug.label')}
                                required={true}
                            >
                                <Form.Item
                                    name="slug"
                                    rules={[
                                        {
                                            required: true,
                                            message: t('tripCollection.field.slug.required')
                                        },
                                        { pattern: /^[a-z0-9-_]+$/, message: t('tripCollection.field.slug.pattern') }
                                    ]}
                                    noStyle>
                                    <Input placeholder={t('tripCollection.field.slug.placeholder')} />
                                </Form.Item>
                                <div>{t('tripCollection.field.slug.note')}</div>
                            </Form.Item>
                            {!requiredTripSupportAllLanguages && <Form.Item
                                label={t('tripCollection.field.languages.label')}
                                required={true}
                            >
                                <Form.Item
                                    name="languages"
                                    rules={[
                                        {
                                            required: true,
                                            message: t('tripCollection.field.languages.required')
                                        }
                                    ]}
                                    noStyle>
                                    <Select mode="multiple" allowClear options={languageOptions} onChange={selectedLanguages} />
                                </Form.Item>
                                <div>{t('tripCollection.field.languages.note')}</div>
                            </Form.Item>}
                            <Tabs>
                                {addedLanguages.map((language, index) => {
                                    return <Tabs.TabPane tab={language.label} key={language.value} forceRender>
                                        <Form.Item
                                            label={t('tripCollection.field.title.label')}
                                            name={[language.value, 'title']}
                                            required={true}
                                            rules={
                                                [
                                                    {
                                                        required: true,
                                                        message: t('tripCollection.field.title.required')

                                                    }
                                                ]}
                                        >
                                            <Input placeholder={t('tripCollection.field.title.placeholder')} />
                                        </Form.Item>
                                        <Form.Item
                                            label={t('tripCollection.field.subtitle.label')}
                                            name={[language.value, 'subtitle']}
                                        >
                                            <Input placeholder={t('tripCollection.field.subtitle.placeholder')} />
                                        </Form.Item>
                                        <Form.Item
                                            label={t('tripCollection.field.description.label')}
                                            name={[language.value, 'description']}
                                        >
                                            <Input.TextArea autoSize={{ minRows: 5, maxRows: 10 }} placeholder={t('tripCollection.field.description.placeholder')} />
                                        </Form.Item>
                                        <MediaSelect showMediaDrawer={showMediaDrawer} form={form} wrapper={{
                                            wrapperCol: 16,
                                            required: true,
                                            label: t('tripCollection.field.banner.label')
                                        }} inner={{
                                            name: [language.value, 'banner'],
                                            rules: [
                                                {
                                                    required: true,
                                                    message: t('tripCollection.field.banner.required')
                                                }
                                            ]
                                        }} />
                                        <Form.Item
                                            label={t('tripCollection.field.topics.label')}
                                            name={[language.value, 'topics']}
                                        >
                                            <Select mode="multiple" allowClear options={topicOptions.filter(topicTranslation => {
                                                if (topicTranslation.language === language.value) {
                                                    return true;
                                                } else {
                                                    return false;
                                                }
                                            })} />
                                        </Form.Item>
                                        <SeoFields
                                            name={[language.value]}
                                            layout={layout}
                                            form={form}
                                            showMediaDrawer={showMediaDrawer}
                                        />
                                    </Tabs.TabPane>
                                })}
                            </Tabs>
                        </>
                    </Form>
                    <Drawer contentWrapperStyle={{ width: '1000px', maxWidth: '100%', minWidth: '50%' }} title={t('mediaDrawer.title')} placement="right" onClose={onMediaDrawerClose} open={mediaDrawerVisible}>
                        <EntityMedia entityId={params.entityId} selectMedia={onSelectMedia} />
                    </Drawer>
                </div>
            </Content>
            <MemberFooter />
        </Layout>
    </>;
}

export default TripCollection;